// Sestavení řádků objednávkového formuláře.
//
// Pravidlo: OBJEDNAT lze jen produkt v aktuální nabídce (validity == 1),
// ODEPSAT (reklamovat) lze cokoli, co zákazník objednal v posledních dnech –
// tedy i produkt, který už je mimo sezónu (validity == 0). Proto se řádek
// zobrazí, když je produkt objednatelný NEBO reklamovatelný; u neobjednatelných
// řádků se ve formuláři zamkne jen objednací pole, odpis zůstává aktivní.

export interface ReclamationProduct {
    code?: string;
    quantity?: number;
}

export interface OrderRow {
    code?: string;
    name?: string;
    unit?: string;
    orderCount: number;
    reclamationCount: number;
    orderUnit?: string;
    reclamationAllowedCount: number;
    orderable: boolean;
    [key: string]: any;
}

// validity může z API přijít jako number (1) i jako string ("1") – sjednotíme.
export function isOrderable(product: any): boolean {
    return Number(product?.custom?.validity) === 1;
}

export function buildOrderRows(
    products: any[] | undefined | null,
    reclamationProducts: ReclamationProduct[] | undefined | null,
): OrderRow[] {
    const reclByCode = new Map<string, number>();
    for (const rp of reclamationProducts ?? []) {
        if (rp?.code != null) reclByCode.set(rp.code, rp.quantity ?? 0);
    }

    return (products ?? [])
        .map((product): OrderRow => {
            const reclamationAllowedCount = reclByCode.get(product.code) ?? 0;
            return {
                ...product,
                orderCount: 0,
                reclamationCount: 0,
                orderUnit: product.unit,
                reclamationAllowedCount,
                orderable: isOrderable(product),
            };
        })
        // objednatelné produkty + ty, co jdou odepsat (objednány dřív, teď mimo sezónu)
        .filter(row => row.orderable || row.reclamationAllowedCount > 0)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'cs'));
}

// Řádky se sestaví až po doběhnutí OBOU požadavků, nezávisle na pořadí odpovědí.
// Selhání reklamací nesmí vypadat jako „nic k reklamaci“: nahlásí se přes
// onReclamationError a objednávat jde dál (reklamace zůstanou zamčené).
export async function loadOrderRows(
    fetchProducts: () => Promise<any[]>,
    fetchReclamationProducts: () => Promise<ReclamationProduct[]>,
    onReclamationError: (error: unknown) => void,
): Promise<OrderRow[]> {
    const [products, reclamationProducts] = await Promise.all([
        fetchProducts(),
        Promise.resolve()
            .then(fetchReclamationProducts)
            .catch((error) => {
                onReclamationError(error);
                return [];
            }),
    ]);
    return buildOrderRows(products, reclamationProducts);
}

// Zakázané položky prodejny (zakazy.xml) se nezobrazí vůbec, ani k odpisu.
// Kódy nese dodací adresa v custom.forbiddenCodes, plní je sync GiraModulu.
export function hideForbidden(rows: OrderRow[], shippingAddress: any): OrderRow[] {
    const codes = shippingAddress?.custom?.forbiddenCodes;
    if (!Array.isArray(codes) || codes.length === 0) return rows;
    const forbidden = new Set(codes);
    return rows.filter(row => !forbidden.has(row.code));
}

// Ceny zákazníka (zatím jen mimořádně, COOP V. Meziříčí) nese dodací adresa v custom.prices.
// Ceny jsou bez DPH a orientační. Formulář z nich počítá součet, API je při uložení zapíše
// k objednávce jako snímek. Bez ceníku vrací null a sloupec ani součet se neukáže.
export function customerPrices(shippingAddress: any): Record<string, number> | null {
    const prices = shippingAddress?.custom?.prices;
    if (!prices || typeof prices !== 'object' || Object.keys(prices).length === 0) return null;
    return prices;
}

const priceFormat = new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK' });

export function formatPrice(prices: Record<string, number> | null, row: Pick<OrderRow, 'code' | 'unit'>): string {
    const price = prices?.[row.code];
    if (typeof price !== 'number') return '–';
    return row.unit ? `${priceFormat.format(price)} / ${row.unit}` : priceFormat.format(price);
}

export function formatCzk(value: number): string {
    return priceFormat.format(value);
}

function round2(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

// Stejné pravidlo jako API (giraOrderRules): ceník je za jednotku produktu, balení (bal)
// se přepočte přes množství v obalu (custom.mnobal). Jiné jednotky cenu nemají a do součtu nejdou.
function orderUnitFactor(row: OrderRow): number | null {
    const orderUnit = row.orderUnit ?? row.unit;
    if (!row.unit || orderUnit === row.unit) return 1;
    const packSize = Number(row.custom?.mnobal);
    return orderUnit === 'bal' && Number.isFinite(packSize) && packSize > 0 ? packSize : null;
}

export function orderTotal(rows: OrderRow[], prices: Record<string, number> | null): { total: number; missing: string[] } {
    let total = 0;
    const missing: string[] = [];
    for (const row of rows) {
        const quantity = Number(row.orderCount);
        if (!(quantity > 0)) continue;
        const price = row.code ? prices?.[row.code] : undefined;
        const factor = orderUnitFactor(row);
        if (typeof price !== 'number' || factor === null) {
            missing.push(row.name ?? row.code ?? '');
            continue;
        }
        total += round2(quantity * factor * price);
    }
    return { total: round2(total), missing };
}

// Minimální objednávka v Kč bez DPH z custom.minimumOrder dodací adresy, jen u adres s ceníkem.
export function minimumOrder(shippingAddress: any): number | null {
    if (!customerPrices(shippingAddress)) return null;
    const minimum = Number(shippingAddress?.custom?.minimumOrder);
    return Number.isFinite(minimum) && minimum > 0 ? minimum : null;
}

export function pragueDay(date: Date | string): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Prague', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date(date));
}

const refId = (ref: any): string | undefined =>
    !ref ? undefined : typeof ref === 'string' ? ref : (ref._id ?? ref.id);

// Dodatečná objednávka jde jen ke klasické objednávce stejné prodejny vytvořené dnes (hlídá i API).
// Nejnovější první, formulář dodatečnou objednávku přiřadí k ní.
export function supplementParents(orders: any[] | null | undefined, shippingAddress: any, now: Date = new Date()): any[] {
    const shippingId = refId(shippingAddress);
    const today = pragueDay(now);
    return (orders ?? []).filter(order =>
        order?.createdAt
        && refId(order.addresses?.shipping) === shippingId
        && order.status !== 'cancelled'
        && order.custom?.orderType !== 'supplement'
        && pragueDay(order.createdAt) === today,
    ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}
